import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import LiveCard from "@/components/feature/LiveCard";
import FilterBar from "@/components/feature/FilterBar";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { categories } from "@/mocks/categories";
import { filterLives, type FilterOptions } from "@/utils/liveData";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

export default function Category() {
  const { categoryName = "" } = useParams();
  const category = categories.find((cat) => cat.key === categoryName);
  const [filters, setFilters] = useState<FilterOptions>({
    region: "all",
    status: "all",
    level: "all",
    sort: "hot",
  });
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const title = category ? `${category.name}直播 - 蜜梨直播` : "直播分类 - 蜜梨直播";

  usePageMeta({
    title,
    description: category
      ? `${category.intro}在蜜梨直播发现更多${category.name}优质直播间。`
      : "蜜梨直播分类页，按兴趣浏览娱乐、游戏、才艺等直播内容。",
    keywords: category ? `${category.name},蜜梨直播,在线直播,互动直播` : "蜜梨直播分类,直播分类",
  });

  const result = useMemo(() => {
    if (!category) return [];
    return filterLives({ ...filters }).filter((item) => item.categoryKey === category.key);
  }, [category, filters]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = result.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateFilters = (next: FilterOptions) => {
    setFilters(next);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ region: "all", status: "all", level: "all", sort: "hot" });
    setPage(1);
  };

  if (!category) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="w-full flex-1 px-4 py-16 md:px-8">
          <EmptyState
            title="未找到该分类"
            description="你访问的分类不存在，可以返回首页浏览其他直播内容。"
            linkTo="/"
          />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />

      <main className="w-full flex-1 px-4 py-6 md:px-8">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-base" />
          <span className="text-foreground-700">直播分类</span>
          <i className="ri-arrow-right-s-line text-base" />
          <span className="font-medium text-foreground-950">{category.name}</span>
        </nav>

        <div className="mt-4 flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className={`${category.icon} text-2xl`} />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-foreground-950">
              {category.name}
            </h1>
            <p className="mt-1 text-sm text-foreground-500">{category.intro}</p>
          </div>
        </div>

        <div className="mt-6 flex gap-6">
          <aside className="hidden w-64 shrink-0 lg:block">
            <h2 className="mb-3 font-heading text-sm font-bold text-foreground-950">筛选条件</h2>
            <FilterBar value={filters} onChange={updateFilters} />
          </aside>

          <div className="min-w-0 flex-1">
            <div className="mb-4 flex items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-bold text-primary-600">{result.length}</span> 个直播间
              </p>
              <button
                type="button"
                onClick={() => setFilterOpen((prev) => !prev)}
                className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line text-base" />
                筛选
              </button>
            </div>

            {filterOpen && (
              <div className="mb-4 lg:hidden">
                <FilterBar value={filters} onChange={updateFilters} />
              </div>
            )}

            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {pageItems.map((live) => (
                    <LiveCard key={live.id} live={live} />
                  ))}
                </div>
                <Pagination current={currentPage} total={totalPages} onChange={setPage} />
              </>
            ) : (
              <EmptyState
                title="没有匹配的直播间"
                description="当前筛选条件下暂无内容，试试调整筛选条件或重置后再看看。"
                actionLabel="重置筛选"
                onAction={resetFilters}
              />
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}